import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  AsyncValidatorFn,
  FormArray,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { cloneDeep, maxBy } from 'lodash';
import moment from 'moment';
import { take, takeWhile } from 'rxjs/operators';
import { ClasseProfissionalService } from '../../../../services/classe-profissional/classe-profissional.service';
import { ProfissaoService } from '../../../../services/profissao/profissao.service';
import { LoginStorage } from '../../../../login/login.storage';
import { ModalComponent } from '../../../../shared/modais/modal/modal.component';
import { FichaService } from '../../../ficha-veiculo.service';
import { FichaCadastro } from '../../ficha-cadastro';
import { Avalista } from '../../model/avalista';
import { VeiculoEmSeuNome } from '../../model/veiculo';
import { ModalInfoConjugeAvalistaComponent } from './modal-info-conjuge-avalista/modal-info-conjuge-avalista.component';
import { CustomModalService } from '../../../../shared/modais/custom-modal/custom-modal.service';
import { onlyDigits } from '../../../../shared/helper/utils';

@Component({
  selector: 'app-dados-avalista',
  templateUrl: './dados-avalista.component.html',
  styleUrls: ['./dados-avalista.component.scss'],
})
export class DadosAvalistaComponent implements AfterViewInit, OnChanges, OnDestroy {
  public TIPO_SOLIC_CLIENTE = 'CLIENTE';
  public TIPO_SOLIC_OUTRA = 'OUTRA';

  @ViewChild('modalAvalista', { static: true }) modalAvalista: ModalComponent;

  @Input() formulario: FormArray;
  @Input() isAvalista: boolean;
  @Input() isValidatingClienteCpf: boolean;
  @Input() fichaCadastro: FichaCadastro;
  @Input() cpfCliente: string;
  @Input() celularCliente: string;

  @Output() avalistaConjugeEmit = new EventEmitter();

  idLojista: number;
  avalistaRecuperado: Avalista;
  indexAvalistaRecuperado: number;

  formValidVeiculoEmSeuNome: boolean;

  isAlive = true;
  isValidatingAvalistaCpf = false;

  dataRange = {
    min: 18,
    max: 85,
    message: 'A idade deve ser entre 18 e 85 anos',
  };

  classesProfissionais: any = [];
  profissoesConjuge: any[];
  public errorMessage: string = null;

  constructor(
    private readonly fb: FormBuilder,
    private readonly fichaService: FichaService,
    private readonly loginStorage: LoginStorage,
    private readonly cd: ChangeDetectorRef,
    private readonly classeProfissionalService: ClasseProfissionalService,
    private readonly modal: CustomModalService,
    private readonly profissaoService: ProfissaoService
  ) {}

  ngAfterViewInit() {
    this.addAvalista();
    this.initDefaultObservers();
  }

  ngOnChanges(changes: SimpleChanges) {
    this.cd.detectChanges();
  }

  ngOnDestroy() {
    this.isAlive = false;
  }

  addAvalista(): void {
    this.formulario.push(this.getNewForm());
  }

  emitAvalistaConjuge(index) {
    this.avalistaConjugeEmit.next(this.isAvalistaConjuge);

    if (this.isConjugeSelected(index) && this.fichaCadastro.isPesados) {
      this.modal.show(ModalInfoConjugeAvalistaComponent, {
        class: 'modal-sm2',
        keyboard: false,
        ignoreBackdropClick: true,
      });
    }
  }

  initDefaultObservers() {
    this.fillClassesProfissionais();
  }

  private fillClassesProfissionais(): void {
    this.classeProfissionalService
      .classes()
      .pipe(take(1))
      .subscribe((res) => (this.classesProfissionais = res));
  }

  private fillProfissoesConjuge(res: number, clear = true): void {
    this.profissaoService
      .profissoes(res)
      .pipe(takeWhile(() => this.isAlive))
      .subscribe((res) => (this.profissoesConjuge = res.profissoes));
  }

  onClasseProfissionalChange(index) {
    this.formulario.controls[index].get('cargoConjuge').reset();
    this.formulario.controls[index].get('cargoConjuge').enable();
    this.fillProfissoesConjuge(
      this.formulario.controls[index].get('classeProfConjuge').value
    );
  }

  private getNextIndex(): number {
    const value: any = maxBy(this.formulario.value, 'index');
    if (value) {
      return value.index + 1;
    }
    return 0;
  }

  getNewForm(): FormGroup {
    const index = this.getNextIndex();
    return this.fb.group({
      index,
      nomeConjuge: [''],
      cpf: ['', [Validators.required, this.duplicadoValidator(index, 'cpf')]],
      dataNascimento: ['', [Validators.required]],
      celular: this.fb.control('', {
        validators: [
          Validators.required,
          this.duplicadoValidator(index, 'celular'),
        ],
        asyncValidators: [this.validCelphone()],
        updateOn: 'blur',
      }),
      renda: ['', [Validators.required]],
      grauParentesco: ['', [Validators.required]],
      classeProfConjuge: [''],
      cargoConjuge: [''],
      veiculoEmSeuNome: this.fb.group({
        isVeiculoEmMeuNome: [false],
        veiculoPlaca: [
          '',
          [this.duplicadoValidator(index, 'veiculoEmSeuNome.veiculoPlaca')],
        ],
        isNaoSeiPlaca: [false],
        veiculoCategoria: [null],
        veiculoMarca: [null],
        veiculoAno: [null],
        veiculoModelo: [null],
        veiculoVersao: [null],
      }),
      temProximo: false,
    });
  }

  private duplicadoValidator(index: number, model: string): ValidatorFn {
    return (control: AbstractControl): Validators | null => {
      const getValueOfModel = (valueCampo: any, campos: string[]) => {
        const camposAux = cloneDeep(campos);
        if (!camposAux[0] || !valueCampo) {
          return valueCampo || null;
        }
        const valueCampoAlterado = valueCampo[camposAux[0]];
        camposAux.splice(0, 1);
        return getValueOfModel(valueCampoAlterado, camposAux);
      };

      const value: VeiculoEmSeuNome = control.value;
      if (!value) {
        return null;
      }
      const campos = model.split('.');
      const values = this.formulario.value.filter((avalista) => {
        if (avalista.index === index) {
          return false;
        }
        const valueAvalista = getValueOfModel(avalista, campos);
        return valueAvalista === value;
      });

      if (values && values.length) {
        return { duplicidade: true };
      }

      return null;
    };
  }

  getCpfAvalista(index: number) {
    const { cpf } = this.formulario.controls[index].value;

    if (cpf) {
      if (this.cpfCliente && cpf === this.cpfCliente) {
        return;
      }
      this.isValidatingAvalistaCpf = true;
      this.idLojista = this.fichaCadastro.IDLojista;
      if (
        this.fichaCadastro.solicitanteTipo === this.TIPO_SOLIC_CLIENTE ||
        this.fichaCadastro.solicitanteTipo === this.TIPO_SOLIC_OUTRA
      ) {
        this.idLojista = this.loginStorage.agente.lojaBalcaoId;
      }
      this.fichaService
        .recuperarDadosFicha(this.formatCpf(cpf), this.idLojista)
        .subscribe(
          (res) => {
            this.isValidatingAvalistaCpf = false;
            this.preencheAvalista(index, res);
            this.modalAvalista.show();
          },
          (err) => {
            this.isValidatingAvalistaCpf = false;
          },
          () => {
            this.isValidatingAvalistaCpf = false;
          }
        );
    }
  }

  preencheAvalista(index, res) {
    this.indexAvalistaRecuperado = index;
    this.avalistaRecuperado = new Avalista();
    this.avalistaRecuperado.cpf = res.clientes[0].cpf;
    this.avalistaRecuperado.dataNascimento = moment(
      new Date(res.clientes[0].dataNascimento)
    ).format('DD/MM/YYYY');
    this.avalistaRecuperado.telefones.celular =
      res.clientes[0].telefones[0].ddd + res.clientes[0].telefones[0].telefone;
    this.avalistaRecuperado.renda = res.clientes[0].profissional[0].salario;
  }

  formatCpf(data) {
    if (data) {
      return data.replace(/[\.\-]/g, '');
    }
  }

  public getVeiculos(index: number): VeiculoEmSeuNome[] {
    const avalistas = this.formulario.value;
    return avalistas
      .filter((v, i) => i !== index)
      .map((m) => m.veiculoEmSeuNome);
  }

  public changeFormVeiculoEmSeuNome(index: number, value: any): void {
    if (!value) {
      return;
    }
    this.formulario.controls[index]
      .get('veiculoEmSeuNome')
      .patchValue(value.values);
  }

  isValid(): boolean {
    return (
      this.formulario.valid && !this.cpfsAvalistas.includes(this.cpfCliente)
    );
  }

  get cpfsAvalistas(): string[] {
    const avalistas = this.formulario.value;
    return avalistas.map((m) => m.cpf);
  }

  continuar() {
    this.modalAvalista.hide();
  }

  recuperarDadosAvalista() {
    if (this.fichaCadastro._avalistas[this.indexAvalistaRecuperado]) {
      this.fichaCadastro._avalistas[this.indexAvalistaRecuperado] =
        this.avalistaRecuperado;
    } else {
      this.fichaCadastro._avalistas.push(this.avalistaRecuperado);
    }
    this.formulario.controls[this.indexAvalistaRecuperado].patchValue(
      this.avalistaRecuperado
    );
    const celular =
      this.avalistaRecuperado.telefones &&
      this.avalistaRecuperado.telefones.celular;
    this.formulario.controls[this.indexAvalistaRecuperado]
      .get('celular')
      .setValue(celular);
    this.modalAvalista.hide();
  }

  onClickAddAvalista(index: number) {
    const avalista = this.formulario.controls[index].value;
    if (avalista.temProximo) {
      this.addAvalista();
      return;
    }
    this.formulario.removeAt(index + 1);
    if (
      this.formulario.controls[index + 1] &&
      this.formulario.controls[index + 1].value
    ) {
      this.formulario.controls[index].get('temProximo').setValue(true);
    }

    this.avalistaConjugeEmit.next(this.isAvalistaConjuge);
  }

  public descricaoAddAvalista(index: number): string {
    const descricao = !index ? 'Segundo' : 'Terceiro';
    return `Adicionar ${descricao} Avalista`;
  }

  public hasConjuge(index: number) {
    return this.formulario.value.some(
      (avalista, indexAvalista) =>
        avalista &&
        Number(avalista.grauParentesco) === 1 &&
        indexAvalista !== index
    );
  }

  public isConjugeSelected(index: number) {
    return this.formulario.value.some(
      (avalista, indexAvalista) =>
        avalista &&
        Number(avalista.grauParentesco) === 1 &&
        indexAvalista === index
    );
  }

  get isAvalistaConjuge() {
    return this.formulario.value.some(
      (avalista) => avalista && Number(avalista.grauParentesco) === 1
    );
  }

  get classesProfissionaisConjuge() {
    return this.classesProfissionais;
  }

  getDddAndCelular(value): { ddd: string; telefone: string } {
    if (!value || value === '') return { ddd: '', telefone: '' };
    const digits = onlyDigits(value);
    const ddd = digits.substring(0, 2);
    const telefone = digits.substring(2);
    return { ddd, telefone };
  }

  validCelphone(): AsyncValidatorFn {
    this.errorMessage = null;
    return (control: AbstractControl): Promise<ValidationErrors | null> => {
      if (!control.value || control.value.length === 0) {
        return Promise.resolve(null);
      }
      const dddCelular = this.getDddAndCelular(control.value);
      return this.fichaService
        .verifyCellPhone(dddCelular.ddd, dddCelular.telefone)
        .pipe(take(1))
        .toPromise()
        .then((res) => {
          if (res.status !== 'FAIL') {
            this.errorMessage = null;
            return null;
          }
          this.errorMessage = res.messages[0];
          return { genericErrorMessage: res.messages[0] };
        });
    };
  }

  clearErrorMessage() {
    this.errorMessage = null;
  }
}
